import {useEffect, useRef, useState} from 'react';

import {focusIsFree} from './useRetryFocus';

/**
 * Holds a failed read's failure, and the Retry pressed on it, on screen until
 * the retry has its answer (COM-383, shared by COM-439).
 *
 * React Query 4 clears `error` and returns to loading while a query with no
 * cached data refetches, so a failure shown only on `error` would give way to
 * the loading state and take the focused Retry with it. `retrying` keeps it:
 * it is true from the press until the result the retry ended with has been
 * passed in. React Query re-renders the component that reads the query on a
 * timer, so a component given the query's `data` and `error` can render the
 * settled request first, with the values of a request still in flight. The
 * retry is therefore `settled`, not over, until `error` or `data` arrives, and
 * it ends during render rather than in an effect, so the commit that shows the
 * result no longer holds the failure for it.
 *
 * `retry` sends one request however often it is pressed: the ref blocks a
 * second press synchronously, before the busy state has rendered.
 * `retryPending` is true while the request is in flight, for `aria-busy`.
 *
 * A retry that loads the data removes the Retry that had focus. `loadedRef` is
 * for the control that takes its place, such as the select a list fills: it is
 * given focus then, so the person carries on from where they pressed, unless
 * they have moved to another control while the request was in flight.
 */
export default function useRetryHold({data, error, refetch}) {
  // 'idle', 'pending' while the request is in flight, then 'settled'.
  const [phase, setPhase] = useState('idle');
  const pending = useRef(false);

  const retry = () => {
    if (pending.current) return;

    pending.current = true;
    setPhase('pending');
    // React Query's refetch resolves rather than rejects on failure.
    void refetch().finally(() => {
      pending.current = false;
      setPhase('settled');
    });
  };

  if (phase === 'settled' && (error || data !== undefined)) {
    setPhase('idle');
  }

  const loadedRef = useRef(null);
  const previousPhase = useRef(phase);
  useEffect(() => {
    const loaded =
      previousPhase.current !== 'idle' && phase === 'idle' && !error && data !== undefined;
    if (loaded && focusIsFree()) {
      loadedRef.current?.focus();
    }
    previousPhase.current = phase;
  }, [phase]);

  return {retrying: phase !== 'idle', retryPending: phase === 'pending', retry, loadedRef};
}
